import classNames from "classnames";
import {
    CsvIcon,
    DocIcon,
    DocumentIcon,
    JpgIcon,
    PdfIcon,
    PngIcon,
    XlsIcon,
} from "@icons";
import styles from "./AgentChat.module.scss";

interface AgentChatAttachmentChipProps {
    fileName: string;
    mimeType: string;
    mode: "live" | "static";
    progress?: number;
    status?: "uploading" | "ready" | "error";
    errorMessage?: string;
    onRemove?: () => void;
}

const FileIcon = ({ mimeType }: { mimeType: string }) => {
    const lower = mimeType.toLowerCase();
    const className = styles.attachmentIcon;
    if (lower === "application/pdf") {
        return <PdfIcon className={className} />;
    }
    if (lower === "image/jpeg" || lower === "image/jpg") {
        return <JpgIcon className={className} />;
    }
    if (lower === "image/png") {
        return <PngIcon className={className} />;
    }
    if (lower === "text/csv") {
        return <CsvIcon className={className} />;
    }
    if (
        lower === "application/vnd.ms-excel" ||
        lower.includes("spreadsheetml") ||
        lower === "application/vnd.ms-excel.sheet.macroenabled.12"
    ) {
        return <XlsIcon className={className} />;
    }
    if (lower === "application/msword" || lower.includes("wordprocessingml")) {
        return <DocIcon className={className} />;
    }
    return <DocumentIcon className={className} />;
};

export const AgentChatAttachmentChip: React.FC<
    AgentChatAttachmentChipProps
> = ({
    fileName,
    mimeType,
    mode,
    progress,
    status,
    errorMessage,
    onRemove,
}) => {
    const isLive = mode === "live";
    const isUploading = isLive && status === "uploading";
    const isError = isLive && status === "error";
    const progressPercent = Math.max(0, Math.min(1, progress ?? 0)) * 100;

    return (
        <span
            className={classNames(styles.attachmentChip, {
                [styles.attachmentChipUploading]: isUploading,
                [styles.attachmentChipError]: isError,
            })}
            title={isError ? errorMessage : fileName}
        >
            <FileIcon mimeType={mimeType} />
            <span className={styles.attachmentName}>{fileName}</span>
            {isLive && onRemove && (
                <button
                    type="button"
                    className={styles.attachmentRemove}
                    aria-label={`Remove ${fileName}`}
                    onClick={onRemove}
                >
                    ×
                </button>
            )}
            {isUploading && (
                <span
                    className={styles.attachmentProgressBar}
                    style={{ width: `${progressPercent}%` }}
                />
            )}
        </span>
    );
};
